import React, { useEffect, useMemo, useState } from "react";

import BaseBarChart from "components/Chart/BaseBarChart";
import DivisionBox from "../../ClaimsMis/DivisionBox";
import BaseDoughnutChart from "components/Chart/BaseDoughnutChart";
import ProgressBar from "../../ClaimsMis/ProgressBar";
import RenderSummary from "../../ClaimsMis/RenderSummary";
import Header from "../../ClaimsMis/Header";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useNavigate } from "react-router";
import EmptyDataLottie from "components/EmptyDataLottie";
import useFetchClaims from "hooks/useFetchClaims";
import { ClaimDonwloadBtn, ClaimSummaryButton, SummaryBtnsWrapper } from "./styles";
import { usePDF } from 'react-to-pdf';
import downlaodClaimsIcon from "../../../../src/assets/svgs/downlaodClaimsIcon.svg";
import { Box } from "@mui/material";
import { OverlayLoader } from "components/core";


function Claims() {
  const { toPDF, targetRef } = usePDF({filename: 'claims.pdf'});
  const { claims, loading } = useFetchClaims(true);

  const [data, setData] = useState({});
  const [noData, setNoData] = useState(false);

  const navigate = useNavigate();
  const redirectToClaimMIS = () => {
    navigate("/v1/claims/mis");
  };

  useEffect(async () => {
    if (!claims) return;
    setData(claims);
    if (
      (claims?.claim_analysis?.labels,
      claims?.claim_summary["Total Claims Submitted"])
    ) {
      setNoData(true);
    }
  }, [claims]);

  const getDatasets = (object_value) => {
    const { labels, ...datasets } = object_value;
    return datasets;
  };

  const sortedClaimSummary = useMemo(() => {
    if (!data.claim_summary) return [];
    const excludedKeys = ["updated_at", "Total Claims Submitted"];
    return Object.entries(data.claim_summary)
      .filter(
        ([key]) => key !== "updated_at" && key !== "Total Claims Submitted"
      )
      .sort(([, a], [, b]) => b - a)
      .map(([key]) => key);
  }, [data]);

  const adjustedClaimAnalysis = useMemo(() => {
    if (!data.claim_analysis) return {};

    const { labels, ...datasets } = data.claim_analysis;

    const newDatasets = Object.keys(datasets).reduce((acc, key) => {
      return {
        ...acc,
        [key]: {
          amounts: new Array(sortedClaimSummary.length).fill(0), // Initialize amounts array
          counts: new Array(sortedClaimSummary.length).fill(0), // Initialize counts array
        },
      };
    }, {});

    sortedClaimSummary.forEach((sortedLabel, newIndex) => {
      const originalIndex = labels.indexOf(sortedLabel);
      if (originalIndex !== -1) {
        Object.keys(datasets).forEach((datasetKey) => {
          newDatasets[datasetKey].amounts[newIndex] =
            datasets[datasetKey].amounts[originalIndex];
          newDatasets[datasetKey].counts[newIndex] =
            datasets[datasetKey].counts[originalIndex];
        });
      }
    });

    return {
      labels: sortedClaimSummary,
      ...newDatasets,
    };
  }, [data, sortedClaimSummary]);

  if(loading) {
    return <OverlayLoader />
  }

  return (
    <>
      <div style={{ maxWidth: "1200px" }}>
        {noData ? (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "4rem" }}
          >
            <DivisionBox
              style={{
                height: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Header last_updated_at={data?.claim_summary?.updated_at} />
                <SummaryBtnsWrapper>
                  <ClaimDonwloadBtn
                    variant="contained"
                    onClick={() => toPDF()}
                  >
                    <img src={downlaodClaimsIcon} />
                  </ClaimDonwloadBtn>
                  <ClaimSummaryButton
                    variant="contained"
                    onClick={redirectToClaimMIS}
                    endIcon={<ArrowForwardIcon />}
                  >
                    View Detailed Summary
                  </ClaimSummaryButton>
                </SummaryBtnsWrapper>
              </Box>
              <RenderSummary data={data.claim_summary} />
            </DivisionBox>
            <Box
              sx={{ display: "flex", gap: "4rem", flexDirection: "column" }}
              ref={targetRef}
            >
              <section style={{ display: "flex", gap: "2rem" }}>
                <div style={{ width: "60%" }}>
                  <DivisionBox>
                    <BaseBarChart
                      labels={adjustedClaimAnalysis?.labels}
                      datasets={getDatasets(adjustedClaimAnalysis)}
                      title="Claim Amount by Status"
                      showLabels={false}
                      showBarContentBorders={true}
                    />
                  </DivisionBox>
                </div>
                <div style={{ width: "40%" }}>
                  <DivisionBox>
                    <ProgressBar data={data?.ailment_wise} />
                  </DivisionBox>
                </div>
              </section>
              <section>
                <DivisionBox>
                  <BaseBarChart
                    labels={data?.amount_band?.labels}
                    datasets={getDatasets(data?.amount_band)}
                    title="Claims Settled by Month"
                    hideLegend
                    stacked
                    combineDatasets
                  />
                </DivisionBox>
              </section>
              <section style={{ display: "flex", gap: "2rem" }}>
                <DivisionBox>
                  <BaseDoughnutChart
                    labels={data?.relation_wise?.labels}
                    cutout={100}
                    radius={160}
                    datasets={getDatasets(data?.relation_wise)}
                    title="Number of Claims Settled by Relation"
                  />
                </DivisionBox>
                <DivisionBox>
                  <BaseDoughnutChart
                    labels={data?.age_band?.label}
                    radius={220}
                    datasets={getDatasets(data?.age_band)}
                    title="Number of Claims Settled by Age-Band"
                  />
                </DivisionBox>
              </section>
            </Box>
          </div>
        ) : (
          <EmptyDataLottie text="No Data Found" />
        )}
      </div>
    </>
  );
}

export default Claims;
